/**
 * Network Exec Tab — Phase 1c.2
 *
 * Embedded inside SuperAdmin Control Tower as a dedicated tab.
 * Aggregates the four governance surfaces:
 *   • Overdue callback queue
 *   • Holdback panel (admin view)
 *   • Dispute drawer (admin view)
 *   • Override re-review queue
 *   • Mode flip control
 *
 * Soft-mode default. Banner already mounted by Control Tower.
 */
import { useMemo, useState } from "react";
import { ShieldAlert, ShieldCheck, ListChecks } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OverdueCallbackQueue } from "./OverdueCallbackQueue";
import { OverrideRereviewQueue } from "./OverrideRereviewQueue";
import { HoldbackPanel } from "./HoldbackPanel";
import { DisputeDrawer } from "./DisputeDrawer";
import { EnforcementModeFlipDialog } from "./EnforcementModeFlipDialog";
import { useAppData } from "@/data/AppDataContext";
import {
  getNetworkEnforcementMode,
  getOverdueCallbackQueue,
  getSuggestionStats,
  deriveAllHoldbackStates,
  deriveAllDisputes,
} from "@/data/contractor/networkExecution";

interface Props {
  isSuperAdmin: boolean;
  actorId: string;
  actorRole: string;
}

export function NetworkExecTab({ isSuperAdmin, actorId, actorRole }: Props) {
  const {
    callbacks,
    disputeEvents,
    holdbackLedger,
    softSuggestions,
  } = useAppData();
  const [flipOpen, setFlipOpen] = useState(false);
  const [mode, setMode] = useState(getNetworkEnforcementMode());

  const summary = useMemo(() => {
    const overdue = getOverdueCallbackQueue({
      callbacks,
      disputeEvents,
      holdbackLedger,
    });
    const holdbacks = deriveAllHoldbackStates(holdbackLedger);
    const disputes = deriveAllDisputes(disputeEvents);
    return {
      overdueCount: overdue.length,
      activeHoldbacks: holdbacks.filter((h) => h.status === "active" || h.status === "paused")
        .length,
      openDisputes: disputes.filter((d) => d.status === "open").length,
    };
  }, [callbacks, disputeEvents, holdbackLedger]);

  const suggestionStats = useMemo(() => getSuggestionStats(softSuggestions), [softSuggestions]);

  return (
    <div className="space-y-4">
      {/* Escalation tile */}
      <Card className="p-3" data-testid="escalation-tile">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold flex items-center gap-1.5">
              {mode === "soft" ? (
                <ShieldCheck className="h-4 w-4 text-primary" />
              ) : (
                <ShieldAlert className="h-4 w-4 text-destructive" />
              )}
              Network execution governance
            </h3>
            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
              <span>
                Mode: <Badge variant="outline" className="text-[10px] uppercase">{mode}</Badge>
              </span>
              <span>
                {summary.overdueCount} overdue • {summary.activeHoldbacks} active holdbacks •{" "}
                {summary.openDisputes} open disputes
              </span>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!isSuperAdmin}
            onClick={() => setFlipOpen(true)}
            title={isSuperAdmin ? "" : "Super admin only"}
          >
            <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Change mode
          </Button>
        </div>
        {suggestionStats.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/60">
            <div className="text-[11px] uppercase text-muted-foreground mb-1.5 flex items-center gap-1">
              <ListChecks className="h-3 w-3" /> Soft-enforcement signal
            </div>
            <ul className="text-xs space-y-0.5">
              {suggestionStats.map((s) => (
                <li key={s.kind} className="flex justify-between gap-2">
                  <span className="font-mono text-muted-foreground">{s.kind}</span>
                  <span>
                    {s.total} total • acc{" "}
                    {s.acceptanceRate === null
                      ? "—"
                      : `${Math.round(s.acceptanceRate * 100)}%`}{" "}
                    • ovr{" "}
                    {s.overrideRate === null
                      ? "—"
                      : `${Math.round(s.overrideRate * 100)}%`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Queues */}
      <section className="space-y-2">
        <h4 className="text-xs uppercase text-muted-foreground px-1">Overdue callbacks</h4>
        <OverdueCallbackQueue />
      </section>

      <section className="space-y-2">
        <h4 className="text-xs uppercase text-muted-foreground px-1">Holdbacks</h4>
        <HoldbackPanel />
      </section>

      <section className="space-y-2">
        <h4 className="text-xs uppercase text-muted-foreground px-1">Disputes</h4>
        <DisputeDrawer />
      </section>

      <section className="space-y-2">
        <h4 className="text-xs uppercase text-muted-foreground px-1">Override re-review</h4>
        <OverrideRereviewQueue />
      </section>

      <EnforcementModeFlipDialog
        open={flipOpen}
        onOpenChange={setFlipOpen}
        actorId={actorId}
        actorRole={actorRole}
        isSuperAdmin={isSuperAdmin}
        onChanged={(m) => setMode(m)}
      />
    </div>
  );
}
